import { Component, ViewChild } from '@angular/core';
import { ControlContainer, FormsModule, NgForm, NgModelGroup } from '@angular/forms';

import { DateTimePreset, IDateTimeGroup, ISelectOption, TimeframePreset } from 'app/models/filter-form.models';
import { Month, WeekDay } from 'app/models/filter.models';
import { FilterService } from 'app/services/filter.service';
import { makeControlDirty } from 'app/utils/form.utils';
import { CitationDateFilterComponent } from './citation-date-filter/citation-date-filter.component';
import { FilterItemComponent } from '../filter-item/filter-item.component';
import { MatFormField } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { NgFor } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { TimeFilterComponent } from './time-filter/time-filter.component';
import { TimeframePresetFilterComponent } from './timeframe-preset-filter/timeframe-preset-filter.component';

@Component({
  selector: 'app-time-tab',
  templateUrl: './time-tab.component.html',
  styleUrls: ['./time-tab.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [
    FormsModule,
    CitationDateFilterComponent,
    FilterItemComponent,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    TimeFilterComponent,
    TimeframePresetFilterComponent,
  ],
})
export class TimeTabComponent {
  @ViewChild('time') ngModelGroup!: NgModelGroup;

  dateTimeGroup: IDateTimeGroup = {
    months: undefined,
    daysOfWeek: undefined,
  };

  weekDays: ISelectOption<WeekDay>[] = [
    { value: WeekDay.MONDAY, viewValue: 'Monday' },
    { value: WeekDay.TUESDAY, viewValue: 'Tuesday' },
    { value: WeekDay.WEDNESDAY, viewValue: 'Wednesday' },
    { value: WeekDay.THURSDAY, viewValue: 'Thursday' },
    { value: WeekDay.FRIDAY, viewValue: 'Friday' },
    { value: WeekDay.SATURDAY, viewValue: 'Saturday' },
    { value: WeekDay.SUNDAY, viewValue: 'Sunday' },
  ];

  months: ISelectOption<Month>[] = [
    { value: Month.JANUARY, viewValue: 'January' },
    { value: Month.FEBRUARY, viewValue: 'February' },
    { value: Month.MARCH, viewValue: 'March' },
    { value: Month.APRIL, viewValue: 'April' },
    { value: Month.MAY, viewValue: 'May' },
    { value: Month.JUNE, viewValue: 'June' },
    { value: Month.JULY, viewValue: 'July' },
    { value: Month.AUGUST, viewValue: 'August' },
    { value: Month.SEPTEMBER, viewValue: 'September' },
    { value: Month.OCTOBER, viewValue: 'October' },
    { value: Month.NOVEMBER, viewValue: 'November' },
    { value: Month.DECEMBER, viewValue: 'December' },
  ];

  citationDates: {
    from?: Date | string;
    to?: Date | string;
    fromTime?: string;
    toTime?: string;
  } = {
    from: undefined,
    to: undefined,
    fromTime: undefined,
    toTime: undefined,
  };

  dateTimePreset?: DateTimePreset;
  timeframePreset?: TimeframePreset;

  constructor(private filterService: FilterService) {
    this.initializeFilters();
  }

  get weekDayValue(): string | undefined {
    const selectedWeekDays = this.dateTimeGroup.daysOfWeek?.map(
      (day) => this.weekDays.find(({ value }) => value === day)?.viewValue,
    );

    return selectedWeekDays ? selectedWeekDays.toString() : undefined;
  }

  get monthValue(): string | undefined {
    const selectedMonths = this.dateTimeGroup.months?.map(
      (month) => this.months.find(({ value }) => value === month)?.viewValue,
    );

    return selectedMonths?.length ? selectedMonths.toString() : undefined;
  }

  citationDateChange({
    fromDate,
    toDate,
    fromTime,
    toTime,
    dateTimePreset,
  }: {
    fromDate?: Date | string;
    toDate?: Date | string;
    fromTime?: string;
    toTime?: string;
    dateTimePreset?: DateTimePreset;
  }): void {
    this.citationDates = {
      from: fromDate,
      to: toDate,
      fromTime: fromTime,
      toTime: toTime,
    };

    this.dateTimePreset = dateTimePreset;
    this.timeframePreset = undefined;
  }

  timeframePresetChange({
    fromDate,
    toDate,
    timeframePreset,
  }: {
    fromDate?: Date;
    toDate?: Date;
    timeframePreset?: TimeframePreset;
  }): void {
    if (fromDate && toDate) {
      this.citationDates = {
        from: fromDate,
        to: toDate,
      };

      this.dateTimePreset = undefined;
    } else {
      this.citationDates = {
        from: undefined,
        to: undefined,
      };
    }

    this.timeframePreset = timeframePreset;
  }

  clearFilter(filter: 'dayNight' | 'daysOfWeek' | 'months'): () => void {
    return () => {
      makeControlDirty(this.ngModelGroup.control);

      switch (filter) {
        case 'daysOfWeek':
          this.dateTimeGroup.daysOfWeek = undefined;
          break;
        case 'months':
          this.dateTimeGroup.months = undefined;
          break;
      }
    };
  }

  private initializeFilters() {
    const filters = this.filterService.appliedFilters;

    if (!filters.dateFilter) return;

    const { fromTime, toTime, fromDate, toDate, months, daysOfWeek } = filters.dateFilter;

    this.dateTimeGroup = {
      months,
      daysOfWeek,
    };

    this.citationDates = {
      from: fromDate ? fromDate : undefined,
      to: toDate ? toDate : undefined,
      fromTime,
      toTime,
    };
  }
}
